import * as braze from '@braze/web-sdk';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import { configureCloudProviderApiBaseUrls } from '@shared/core-utils';
import { environmentProvider } from '@shared/environment-provider';
import { dataAccessEnvironment } from '@tmx/data-access';
import { v3 } from 'uuid';

import { removeTrackJsMetadata } from '../../partials/remove-trackjs-metadata';
import { setTrackJsMetadata } from '../../partials/set-trackjs-metadata';
import {
  initFlagsmith,
  resetFeatureFlags,
} from '../feature-flags/feature-flags.actions';
import {
  changeBrazeUser,
  handleAuthChange,
  handleUserLoggedOut,
  handleUserSwitchingAccounts,
  initBraze,
} from './account-manager.actions';
import {
  updateBrazeInitialized,
  updateIsOriginalLogin,
  updateUserRoles,
  userAuthenticated$,
  userLoggedOut$,
  userSwitchingAccounts$,
} from './account-manager.repository';

const handleAuthChange$ = createEffect((actions) =>
  actions.pipe(
    ofType(handleAuthChange),
    tapResult(({ user, jwt, isOriginalLogin }) => {
      dispatch(initFlagsmith({ user }));

      if (isOriginalLogin) {
        dispatch(changeBrazeUser({ user }));
      }

      dataAccessEnvironment.authToken = user.authToken;
      dataAccessEnvironment.jwt = jwt.access_token;
      dataAccessEnvironment.tidJwt = user.accessToken ?? '';

      if (user.cloudProvider === 'Azure') {
        configureCloudProviderApiBaseUrls('Azure');
      } else {
        configureCloudProviderApiBaseUrls('AWS');
      }

      setTrackJsMetadata(user);
      updateUserRoles(user.roles);
      updateIsOriginalLogin(isOriginalLogin);

      userAuthenticated$.next();
    })
  )
);

const handleUserLoggedOut$ = createEffect((actions) =>
  actions.pipe(
    ofType(handleUserLoggedOut),
    tapResult(() => {
      userLoggedOut$.next();
      removeTrackJsMetadata();
      braze.removeAllSubscriptions();
      braze.wipeData();
      dispatch(resetFeatureFlags());
    })
  )
);

const handleUserSwitchingAccounts$ = createEffect((actions) =>
  actions.pipe(
    ofType(handleUserSwitchingAccounts),
    tapResult(() => {
      userSwitchingAccounts$.next();
    })
  )
);

const initBraze$ = createEffect((actions) =>
  actions.pipe(
    ofType(initBraze),
    tapResult(() => {
      const brazeInitialized = braze.initialize(
        environmentProvider.getEnvironment().brazeWorkspaceApiKey,
        {
          baseUrl: 'sdk.iad-01.braze.com',
          enableLogging: false,
        }
      );

      if (!brazeInitialized) {
        console.error('Braze failed to initialize');
      }

      updateBrazeInitialized(brazeInitialized);
      updateBrazeInitialized(true);
    })
  )
);

const changeBrazeUser$ = createEffect((actions) =>
  actions.pipe(
    ofType(changeBrazeUser),
    tapResult(({ user }) => {
      const { username, firstName, lastName } = user;
      const usernameAndAccount = username.concat(user.accountName ?? '');
      const userId = v3(
        usernameAndAccount,
        environmentProvider.getEnvironment().brazeUuidHash
      );
      braze.changeUser(userId);
      const brazeUser = braze.getUser();
      if (brazeUser) {
        brazeUser.setCustomUserAttribute('user_roles', user.roles);
        brazeUser.setCustomUserAttribute('user_email', user.username);
        brazeUser.setCustomUserAttribute('screen_width', window.screen.width);
        brazeUser.setCustomUserAttribute('screen_height', window.screen.height);
        brazeUser.setEmail(user.username);
        if (firstName !== null) {
          brazeUser.setFirstName(firstName);
        }
        if (lastName !== null) {
          brazeUser.setLastName(lastName);
        }
      }
    })
  )
);

const effects = [
  handleAuthChange$,
  handleUserLoggedOut$,
  handleUserSwitchingAccounts$,
  initBraze$,
  changeBrazeUser$,
];

export function registerAppEffects() {
  registerEffects(effects);
}

export function deregisterAppEffects() {
  removeEffects(effects);
}
